import {
  type ReactElement,
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
} from 'react';
import styled from 'styled-components';
import { FlexBox } from '@ardoq/layout';
import { dispatchAction } from '@ardoq/rxbeach';
import {
  type HtmlSlideCapture,
  htmlSlideSurfaceRegistered,
  htmlSlideSurfaceUnregistered,
} from './slideCaptureSurface$';

type HtmlSlideCaptureSurfaceProps = {
  capture: HtmlSlideCapture;
  children: ReactNode;
};

const Surface = styled.div`
  width: 100%;
  height: 100%;
`;

const unregisterSurface = (): void =>
  dispatchAction(htmlSlideSurfaceUnregistered());

const useHtmlSurfaceRef = ({
  slideId,
  isBusy,
  hasError,
  isReport,
}: HtmlSlideCapture): RefObject<HTMLDivElement | null> => {
  const surfaceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = surfaceRef.current;
    if (!container) {
      return;
    }
    dispatchAction(
      htmlSlideSurfaceRegistered({
        slideId,
        container,
        isBusy,
        hasError,
        isReport,
      })
    );
    return unregisterSurface;
  }, [slideId, isBusy, hasError, isReport]);

  return surfaceRef;
};

export const HtmlSlideCaptureSurface = ({
  capture,
  children,
}: HtmlSlideCaptureSurfaceProps): ReactElement => {
  const surfaceRef = useHtmlSurfaceRef(capture);
  return <Surface ref={surfaceRef}>{children}</Surface>;
};

export type ReaderCapture = Omit<HtmlSlideCapture, 'slideId'>;

type ReaderCaptureSurfaceProps = ReaderCapture & {
  children: ReactNode;
};

export const ReaderCaptureSurface = ({
  isBusy,
  hasError,
  isReport,
  children,
}: ReaderCaptureSurfaceProps): ReactElement => {
  const surfaceRef = useHtmlSurfaceRef({
    slideId: null,
    isBusy,
    hasError,
    isReport,
  });
  return (
    <FlexBox ref={surfaceRef} flex={1} flexDirection="column" overflow="hidden">
      {children}
    </FlexBox>
  );
};
